import React, { useState, createElement } from 'react';
import { useUser } from '../../context/UserContext';
import { LeafIcon, HeartIcon, SwordIcon, SunIcon, EyeIcon, StarIcon, HandIcon, CheckIcon, LockIcon } from 'lucide-react';
const QuestList: React.FC = () => {
  const {
    userData,
    completeQuest
  } = useUser();
  const [expandedQuest, setExpandedQuest] = useState<string | null>(null);
  // Garden values with their themed names and icons
  const gardenInfo = {
    Wisdom: {
      name: 'Luminous Labyrinth',
      icon: SunIcon,
      color: 'blue'
    },
    Love: {
      name: 'Blooming Sanctuary',
      icon: HeartIcon,
      color: 'pink'
    },
    Respect: {
      name: 'Harmonious Grove',
      icon: HandIcon,
      color: 'purple'
    },
    Bravery: {
      name: 'Steadfast Summit',
      icon: SwordIcon,
      color: 'orange'
    },
    Honesty: {
      name: 'Transparent Pools',
      icon: EyeIcon,
      color: 'yellow'
    },
    Humility: {
      name: 'Whispering Meadow',
      icon: LeafIcon,
      color: 'indigo'
    },
    Truth: {
      name: 'Eternal Compass',
      icon: StarIcon,
      color: 'green'
    }
  };
  // Quests organized by garden
  const quests = [
  // Wisdom Garden Quests
  {
    id: 'wisdom-reflection',
    title: 'Daily Reflection',
    garden: 'Wisdom',
    description: 'Complete a guided reflection on a wisdom teaching',
    duration: '10 min',
    steps: ["Find a quiet space where you won't be disturbed", 'Read the provided wisdom teaching', 'Reflect on how this teaching applies to your life', 'Write your insights in your journal', "Set an intention based on what you've learned"]
  }, {
    id: 'wisdom-mindfulness',
    title: 'Mindful Awareness',
    garden: 'Wisdom',
    description: 'Practice mindful observation of your thoughts',
    duration: '15 min',
    steps: ['Sit comfortably with your back straight', 'Focus on your breath for 2 minutes to center yourself', 'Begin to notice your thoughts as they arise', 'Practice observing without judging or following them', 'Return to your breath whenever you get caught in a thought']
  }, {
    id: 'wisdom-learning',
    title: 'Knowledge Seeker',
    garden: 'Wisdom',
    description: 'Learn something new that expands your perspective',
    duration: '20 min',
    steps: ["Choose a topic you're curious about but unfamiliar with", 'Find a quality source of information on this topic', 'Take notes on key insights and new perspectives', 'Reflect on how this new knowledge changes your understanding', 'Share one insight with someone else']
  },
  // Love Garden Quests
  {
    id: 'love-compassion',
    title: 'Self-Compassion Practice',
    garden: 'Love',
    description: 'Cultivate kindness toward yourself through guided practice',
    duration: '10 min',
    steps: ['Find a comfortable position and take three deep breaths', 'Recall a recent situation where you were hard on yourself', 'Place a hand on your heart and offer yourself understanding', 'Repeat: "This is a moment of suffering. May I be kind to myself."', 'Notice how this practice affects your body and emotions']
  }, {
    id: 'love-gratitude',
    title: 'Gratitude Garden',
    garden: 'Love',
    description: 'Nurture appreciation for the gifts in your life',
    duration: '5 min',
    steps: ['Take a few centering breaths', "Identify three things you're grateful for today", 'For each one, really feel the gratitude in your body', 'Notice how this practice affects your mood and outlook', 'Consider expressing thanks directly if appropriate']
  }, {
    id: 'love-connection',
    title: 'Heart Connection',
    garden: 'Love',
    description: 'Strengthen a relationship through mindful presence',
    duration: '30 min',
    steps: ['Choose someone important in your life', 'Arrange uninterrupted time together', 'Practice being fully present without distractions', 'Ask meaningful questions and listen deeply', 'Express appreciation for something specific about them']
  },
  // Respect Garden Quests
  {
    id: 'respect-boundaries',
    title: 'Boundary Setting',
    garden: 'Respect',
    description: 'Practice establishing healthy boundaries',
    duration: '15 min',
    steps: ['Identify an area where you need stronger boundaries', 'Clarify what specific boundary would support your wellbeing', 'Practice expressing this boundary clearly and compassionately', "Anticipate possible responses and plan how you'll handle them", 'Commit to honoring this boundary consistently']
  }, {
    id: 'respect-listening',
    title: 'Deep Listening',
    garden: 'Respect',
    description: 'Cultivate the art of truly hearing others',
    duration: '20 min',
    steps: ['Find someone willing to share something meaningful', 'Listen without interrupting, planning responses, or judging', 'Notice your own reactions without acting on them', 'Reflect back what you heard to ensure understanding', 'Thank them for sharing their perspective']
  }, {
    id: 'respect-differences',
    title: 'Honoring Differences',
    garden: 'Respect',
    description: 'Expand your appreciation for diverse perspectives',
    duration: '30 min',
    steps: ['Identify a viewpoint different from your own', 'Research to understand this perspective more deeply', 'Notice any resistance or judgment that arises', 'Find at least three valid points within this different view', 'Reflect on how this practice affects your thinking']
  }];
  // Filter quests based on user's current garden and completed quests
  const availableQuests = quests.filter(quest => {
    // Show quests from current garden and previous gardens
    const gardenValues = Object.keys(gardenInfo);
    const currentGardenIndex = gardenValues.indexOf(userData.currentGarden || 'Wisdom');
    const questGardenIndex = gardenValues.indexOf(quest.garden);
    return questGardenIndex <= currentGardenIndex;
  });
  const handleCompleteQuest = (questId: string) => {
    completeQuest(questId);
  };
  const isQuestCompleted = (questId: string) => {
    return userData.completedQuests?.includes(questId) || false;
  };
  return <div className="space-y-6" data-id="element-463">
      {/* Current Garden Quests */}
      <div data-id="element-464">
        <h2 className="text-xl font-semibold mb-4 flex items-center" data-id="element-465">
          {userData.currentGarden && gardenInfo[userData.currentGarden as keyof typeof gardenInfo] ? <>
              {createElement(gardenInfo[userData.currentGarden as keyof typeof gardenInfo].icon, {
            className: `text-${gardenInfo[userData.currentGarden as keyof typeof gardenInfo].color}-600 mr-2`,
            size: 20
          })}
              Current Garden:{' '}
              {gardenInfo[userData.currentGarden as keyof typeof gardenInfo].name}
            </> : <>
              <SunIcon className="text-blue-600 mr-2" size={20} data-id="element-466" />
              Current Garden: Luminous Labyrinth
            </>}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-467">
          {availableQuests.filter(quest => quest.garden === userData.currentGarden).map(quest => {
          const isCompleted = isQuestCompleted(quest.id);
          const isExpanded = expandedQuest === quest.id;
          const garden = gardenInfo[quest.garden as keyof typeof gardenInfo];
          const Icon = garden.icon;
          return <div key={quest.id} className={`border rounded-lg overflow-hidden transition-all ${isCompleted ? `bg-${garden.color}-50 border-${garden.color}-200` : 'bg-white border-gray-200'}`} data-id="element-468">
                  <div className="p-4 cursor-pointer" onClick={() => setExpandedQuest(isExpanded ? null : quest.id)} data-id="element-469">
                    <div className="flex justify-between items-start" data-id="element-470">
                      <div className="flex items-start" data-id="element-471">
                        <div className={`p-2 rounded-full bg-${garden.color}-100 mr-3 mt-1`} data-id="element-472">
                          <Icon size={18} className={`text-${garden.color}-600`} data-id="element-473" />
                        </div>
                        <div data-id="element-474">
                          <h3 className="font-medium" data-id="element-475">{quest.title}</h3>
                          <p className="text-sm text-gray-600" data-id="element-476">
                            {quest.description}
                          </p>
                          <div className="flex items-center mt-1" data-id="element-477">
                            <span className="text-xs text-gray-500 mr-3" data-id="element-478">
                              {quest.duration}
                            </span>
                            {isCompleted && <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full flex items-center" data-id="element-479">
                                <CheckIcon size={12} className="mr-1" data-id="element-480" />
                                Completed
                              </span>}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  {isExpanded && <div className="px-4 pb-4 pt-2 border-t border-gray-100" data-id="element-481">
                      <h4 className="text-sm font-medium mb-2" data-id="element-482">Quest Steps:</h4>
                      <ol className="space-y-1 mb-4" data-id="element-483">
                        {quest.steps.map((step, index) => <li key={index} className="flex items-start text-sm" data-id="element-484">
                            <span className="bg-gray-200 text-gray-700 rounded-full w-5 h-5 flex items-center justify-center text-xs mr-2 flex-shrink-0 mt-0.5" data-id="element-485">
                              {index + 1}
                            </span>
                            <span className="text-gray-700" data-id="element-486">{step}</span>
                          </li>)}
                      </ol>
                      {!isCompleted ? <button onClick={() => handleCompleteQuest(quest.id)} className={`w-full py-2 rounded-lg bg-${garden.color}-600 text-white hover:bg-${garden.color}-700`} data-id="element-487">
                          Complete Quest
                        </button> : <button className="w-full py-2 rounded-lg bg-gray-100 text-gray-500 cursor-not-allowed" disabled data-id="element-488">
                          Already Completed
                        </button>}
                    </div>}
                </div>;
        })}
        </div>
      </div>
      {/* Other Garden Quests */}
      <div data-id="element-489">
        <h2 className="text-xl font-semibold mb-4" data-id="element-490">Other Garden Quests</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-491">
          {Object.entries(gardenInfo).filter(([value]) => value !== userData.currentGarden).map(([value, garden]) => {
          const Icon = garden.icon;
          const gardenQuests = quests.filter(q => q.garden === value);
          const isUnlocked = availableQuests.some(q => q.garden === value);
          return <div key={value} className={`border rounded-lg overflow-hidden ${isUnlocked ? `bg-${garden.color}-50 border-${garden.color}-200` : 'bg-gray-50 border-gray-200'}`} data-id="element-492">
                  <div className="p-4" data-id="element-493">
                    <div className="flex items-center justify-between" data-id="element-494">
                      <div className="flex items-center" data-id="element-495">
                        <div className={`p-2 rounded-full ${isUnlocked ? `bg-${garden.color}-100` : 'bg-gray-200'} mr-3`} data-id="element-496">
                          <Icon size={18} className={isUnlocked ? `text-${garden.color}-600` : 'text-gray-500'} data-id="element-497" />
                        </div>
                        <div data-id="element-498">
                          <h3 className={`font-medium ${isUnlocked ? `text-${garden.color}-800` : 'text-gray-500'}`} data-id="element-499">
                            {garden.name}
                          </h3>
                          <p className="text-sm text-gray-500" data-id="element-500">
                            {isUnlocked ? `${gardenQuests.length} quests available` : 'Garden locked'}
                          </p>
                        </div>
                      </div>
                      {!isUnlocked && <div className="bg-gray-200 p-2 rounded-full" data-id="element-501">
                          <LockIcon size={16} className="text-gray-500" data-id="element-502" />
                        </div>}
                    </div>
                  </div>
                </div>;
        })}
        </div>
      </div>
    </div>;
};
export default QuestList;